import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { ApplicationForm } from '../types';
import { useFormSave } from '../hooks/useFormSave';
import FileUpload from './FileUpload';
import styles from './BudgetSection.module.css';

// Tope máximo adjudicable del concurso (Contest.config.maxAmountCLP). Fuente de
// verdad del mock: PROJECT_RESOURCES.config.maxAmountCLP en seed.ts.
// IMPORTANTE: el tope aplica SOLO al monto solicitado a Avanza UC. El aporte de
// contrapartes va aparte y NO se suma para efectos del límite.
const MAX_AMOUNT_CLP = 36_000_000;

interface BudgetSectionProps {
  readOnly?: boolean;
}

const BudgetSection: React.FC<BudgetSectionProps> = ({ readOnly = false }) => {
  useFormSave({ stepId: 'budget' });

  const {
    control,
    formState: { errors },
  } = useFormContext<ApplicationForm>();

  return (
    <div>
      <h2 className={styles.sectionTitle}>Presupuesto del Proyecto</h2>

      {/*
        DEV NOTE — Info box generado desde Contest.config (no texto libre)
        En producción este bloque debe derivarse de Contest.config, NO ser texto
        libre editable por el admin. El único valor configurable por concurso es:
          · "Monto máximo adjudicable" → Contest.config.maxAmountCLP
        Los demás bullets son texto institucional fijo (normativa UC).
        La constante MAX_AMOUNT_CLP (arriba) refleja PROJECT_RESOURCES.config.maxAmountCLP
        del seed y es la fuente de verdad tanto para este texto como para la
        validación del monto solicitado, evitando que queden desincronizados.
      */}
      <div className={styles.sectionInfoBox}>
        <strong>Montos y usos elegibles:</strong>
        <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
          <li>
            <strong>Monto máximo adjudicable:</strong> $
            {MAX_AMOUNT_CLP.toLocaleString('es-CL')}
          </li>
          <li>
            Todos los montos deberán venir debidamente justificados en el
            presupuesto detallado
          </li>
          <li>
            Los recursos serán rendidos a la universidad según normativa
            institucional
          </li>
        </ul>
      </div>

      <section className={styles.section}>
        <Controller
          name="budget.detailedFile"
          control={control}
          rules={{
            required: 'El archivo detallado del presupuesto es requerido',
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <FileUpload
              sectionType="PROJECT_RESOURCES"
              label="Archivo Excel con Presupuesto Completo"
              description="Adjunte un archivo Excel (.xlsx) con el presupuesto detallado completo, incluyendo todas las categorías, cantidades, justificaciones y cálculos del proyecto."
              options={{
                acceptedTypes: ['.xlsx'],
                maxFiles: 1,
              }}
              value={value ? [value] : []}
              onChange={(fileIds) => onChange(fileIds[0] || '')}
              error={error?.message}
              required
              disabled={readOnly}
              templateFile={{
                url: '/templates/Presupuesto_AvanzaUC2025.xlsx',
                filename: 'Presupuesto_AvanzaUC2025.xlsx',
                label: 'Descargar plantilla del presupuesto',
              }}
            />
          )}
        />

        {/* Monto solicitado a Avanza UC */}
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="montoSolicitado">
            Monto solicitado a Avanza UC (CLP) *
          </label>
          <div className={`${styles.inputContainer} ${styles.montoContainer}`}>
            <span className={styles.currencySymbol}>$</span>
            <Controller
              name="budget.montoSolicitado"
              control={control}
              defaultValue={0}
              rules={{
                required: 'El monto solicitado a Avanza UC es requerido',
                min: { value: 0, message: 'El monto no puede ser negativo' },
                // El tope se compara SOLO contra el monto solicitado; el aporte
                // de contrapartes no se suma para el límite adjudicable.
                max: {
                  value: MAX_AMOUNT_CLP,
                  message: `El monto solicitado no puede superar el máximo adjudicable ($${MAX_AMOUNT_CLP.toLocaleString('es-CL')})`,
                },
              }}
              render={({ field }) => (
                <input
                  id="montoSolicitado"
                  type="text"
                  inputMode="numeric"
                  className={`${styles.input} ${styles.inputWithSymbol} ${styles.montoInput}${errors.budget?.montoSolicitado ? ` ${styles.error}` : ''}${readOnly ? ` ${styles.readOnlyField}` : ''}`}
                  disabled={readOnly}
                  value={Number(field.value || 0).toLocaleString('es-CL')}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '');
                    field.onChange(digits === '' ? 0 : Number(digits));
                  }}
                  onBlur={field.onBlur}
                />
              )}
            />
          </div>
          {errors.budget?.montoSolicitado && (
            <span className={styles.errorMessage}>
              {errors.budget.montoSolicitado.message}
            </span>
          )}
        </div>

        {/* Monto aportado por contrapartes — en pesos (CLP), igual que el
            monto solicitado. (Corrección Denise 2026-07-13: se revierte el
            cambio a USD de la reunión anterior.) */}
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="montoContrapartes">
            Monto aportado por contrapartes (CLP) *
          </label>
          <div className={`${styles.inputContainer} ${styles.montoContainer}`}>
            <span className={styles.currencySymbol}>$</span>
            <Controller
              name="budget.montoContrapartes"
              control={control}
              defaultValue={0}
              rules={{
                required: 'El monto aportado por contrapartes es requerido',
                min: { value: 0, message: 'El monto no puede ser negativo' },
              }}
              render={({ field }) => (
                <input
                  id="montoContrapartes"
                  type="text"
                  inputMode="numeric"
                  className={`${styles.input} ${styles.inputWithSymbol} ${styles.montoInput}${errors.budget?.montoContrapartes ? ` ${styles.error}` : ''}${readOnly ? ` ${styles.readOnlyField}` : ''}`}
                  disabled={readOnly}
                  value={Number(field.value || 0).toLocaleString('es-CL')}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '');
                    field.onChange(digits === '' ? 0 : Number(digits));
                  }}
                  onBlur={field.onBlur}
                />
              )}
            />
          </div>
          {errors.budget?.montoContrapartes && (
            <span className={styles.errorMessage}>
              {errors.budget.montoContrapartes.message}
            </span>
          )}
        </div>

        <div className={styles.warningBox}>
          Verifique que estos valores sean consistentes con lo informado en el
          archivo adjunto.
        </div>
      </section>
    </div>
  );
};

export default BudgetSection;
